import React, { useState, useEffect, useRef } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { IDENTITY, AI_CHAT_SYSTEM_PROMPT } from '../data';

export function useCloseTheLoop() {
  const timeRef = useRef({});
  const startRef = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(e => {
        const id = e.target.id;
        if (!id) return;
        if (e.isIntersecting) {
          startRef.current[id] = Date.now();
        } else if (startRef.current[id]) {
          timeRef.current[id] = (timeRef.current[id] || 0) + (Date.now() - startRef.current[id]);
          delete startRef.current[id];
        }
      });
    }, { threshold: 0.3 });
    document.querySelectorAll('section[id]').forEach(s => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  const getTopSections = () => {
    return Object.entries(timeRef.current)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 2)
      .map(([id]) => id);
  };

  return { getTopSections };
}

export default function SmartContact() {
  const isMobile = useIsMobile();
  const [intent, setIntent] = useState('');
  const [generatedMsg, setGeneratedMsg] = useState('');
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const { getTopSections } = useCloseTheLoop();
  const [closingMsg, setClosingMsg] = useState('');
  const [loadingClosing, setLoadingClosing] = useState(false);

  const generateMessage = async () => {
    if (!intent.trim()) return;
    setGenerating(true);
    try {
      const res = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 1000,
          system: `You draft outreach messages to Mekhal Raj Rajendran on behalf of visitors to his portfolio. Write a concise, personalized cold outreach email (subject line + 3-4 sentence body). Be specific, warm, direct. No fluff.`,
          messages: [{ role: 'user', content: `Draft an outreach message from someone who wants to: ${intent}. Address it to Mekhal. Make it feel genuine and specific.` }],
        }),
      });
      const data = await res.json();
      setGeneratedMsg(data.content?.[0]?.text || '');
    } catch {
      setGeneratedMsg('Could not generate — please write your own or email mekhalrajr@gmail.com directly.');
    }
    setGenerating(false);
  };

  const generateClosing = async () => {
    const top = getTopSections();
    if (!top.length) return;
    setLoadingClosing(true);
    try {
      const res = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 1000,
          system: AI_CHAT_SYSTEM_PROMPT,
          messages: [{ role: 'user', content: `A visitor to my portfolio spent the most time on: ${top.join(' and ')}. Write a personalized 2-sentence closing message from me (Mekhal) that references what they were most interested in and invites them to reach out. Warm, direct, no corporate speak.` }],
        }),
      });
      const data = await res.json();
      setClosingMsg(data.content?.[0]?.text || '');
    } catch { }
    setLoadingClosing(false);
  };

  useEffect(() => {
    const t = setTimeout(generateClosing, 5000);
    return () => clearTimeout(t);
  }, []);

  const copy = () => { navigator.clipboard.writeText(generatedMsg); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  return (
    // Fixed dark background — stays dark in all mood states
    <section id="contact" style={{ padding: '120px 0', background: '#1a1a1a', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
      <div style={{ padding: '0 12%' }}>
        <div className="fade-in" style={{ marginBottom: '64px' }}>
          {/* Prominent section label */}
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', letterSpacing: '0.28em', color: 'rgba(250,250,248,0.45)', textTransform: 'uppercase', marginBottom: '16px' }}>
            Get in Touch
          </div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(36px, 5vw, 72px)', fontWeight: 600, color: '#fafaf8', lineHeight: 1, letterSpacing: '-0.03em', marginBottom: '16px' }}>
            Let's build<br />something.
          </h2>
          {closingMsg && (
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '18px', fontStyle: 'italic', color: 'rgba(250,250,248,0.55)', maxWidth: '560px', lineHeight: 1.5, borderLeft: '2px solid #e8c547', paddingLeft: '20px' }}>
              {closingMsg}
            </div>
          )}
          {loadingClosing && (
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', letterSpacing: '0.15em', color: 'rgba(250,250,248,0.2)', textTransform: 'uppercase' }}>Personalizing...</div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '32px' : '48px' }}>
          {/* Direct contact */}
          <div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', letterSpacing: '0.2em', color: 'rgba(250,250,248,0.3)', textTransform: 'uppercase', marginBottom: '24px' }}>Direct</div>
            <a href={`mailto:${IDENTITY.email}`} style={{ fontFamily: 'var(--font-display)', fontSize: '24px', color: '#e8c547', textDecoration: 'none', display: 'block', marginBottom: '16px', borderBottom: '1px solid rgba(232,197,71,0.3)', paddingBottom: '12px' }}>{IDENTITY.email}</a>
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              {[['LinkedIn', IDENTITY.linkedin], ['GitHub', IDENTITY.github]].map(([label, href]) => (
                <a key={label} href={href} target="_blank" rel="noreferrer" style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', letterSpacing: '0.2em', color: 'rgba(250,250,248,0.4)', textDecoration: 'none', textTransform: 'uppercase', transition: 'color 0.2s' }}
                  onMouseEnter={e => e.target.style.color = 'rgba(250,250,248,0.9)'}
                  onMouseLeave={e => e.target.style.color = 'rgba(250,250,248,0.4)'}
                >{label} ↗</a>
              ))}
            </div>
          </div>

          {/* Smart contact form */}
          <div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', letterSpacing: '0.2em', color: 'rgba(250,250,248,0.3)', textTransform: 'uppercase', marginBottom: '24px' }}>AI-Drafted Outreach</div>
            <div style={{ fontFamily: 'var(--font-body)', fontSize: '13px', color: 'rgba(250,250,248,0.4)', fontWeight: 300, marginBottom: '16px' }}>
              Describe what you're looking for — AI will draft a message for you.
            </div>
            <textarea
              value={intent}
              onChange={e => setIntent(e.target.value)}
              placeholder="e.g. hire him for a senior data role, explore a partnership, learn about Finide..."
              rows={3}
              style={{
                width: '100%', fontFamily: 'var(--font-body)', fontSize: '13px',
                background: 'rgba(250,250,248,0.05)', border: '1px solid rgba(250,250,248,0.1)',
                color: '#fafaf8', padding: '12px', outline: 'none', resize: 'none',
                marginBottom: '12px', lineHeight: 1.6,
              }}
            />
            <button onClick={generateMessage} style={{
              fontFamily: 'var(--font-mono)', fontSize: '10px', letterSpacing: '0.2em', textTransform: 'uppercase',
              color: '#1a1a1a', background: '#e8c547', border: 'none',
              padding: '12px 24px', cursor: 'crosshair', transition: 'opacity 0.2s',
            }}
              onMouseEnter={e => e.target.style.opacity = '0.85'}
              onMouseLeave={e => e.target.style.opacity = '1'}
            >{generating ? 'Drafting...' : 'Draft My Message'}</button>

            {generatedMsg && (
              <div style={{ marginTop: '20px' }}>
                <div style={{ fontFamily: 'var(--font-body)', fontSize: '13px', color: 'rgba(250,250,248,0.7)', lineHeight: 1.8, background: 'rgba(250,250,248,0.04)', padding: '16px', border: '1px solid rgba(250,250,248,0.08)', whiteSpace: 'pre-wrap', marginBottom: '10px' }}>
                  {generatedMsg}
                </div>
                <button onClick={copy} style={{ fontFamily: 'var(--font-mono)', fontSize: '9px', letterSpacing: '0.2em', textTransform: 'uppercase', color: copied ? '#e8c547' : 'rgba(250,250,248,0.4)', background: 'none', border: '1px solid rgba(250,250,248,0.1)', padding: '6px 14px', cursor: 'crosshair', transition: 'all 0.2s' }}>
                  {copied ? '✓ Copied' : 'Copy to clipboard'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
